import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge, Container, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { decodeBase64, encodeBase64, generateUniqueKey } from 'utils/helpers';
import { MilestoneCollaborationsFilters, MilestoneCollaborationSortOptionLabel, MilestoneCollaborationSortOptions } from 'types/collaboration';
import { updateCollaborationsFilters } from 'slices/collaborations/reducer';
import { fetchAllCollaborationsThunk } from 'slices/collaborations/thunk';
import CollaborationListItem from 'Components/collaboration/CollaborationListItem';
import { useIdleTimer } from "react-idle-timer";
import Skeleton from 'react-loading-skeleton';
import InfiniteScroll from 'react-infinite-scroll-component';
import "react-loading-skeleton/dist/skeleton.css";
import FilterIcon from 'assets/icons/mono/FilterIcon';
import Filters, { defaultFiltersValue } from 'Components/collaboration/milestone/Filters';
import TaskFilters from 'Components/collaboration/TaskFilters';
const CollaborationLayout: React.FC<{ children: React.ReactNode | React.ReactNode[] }> = ({ children }) => {
    useDocumentTitle("Collaborations - Campaign");
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const milestoneView: any = useRef();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryFilters = searchParams.get('filters');
    const dispatch: AppDispatch = useDispatch();
    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters, defaultFiltersValue) : defaultFiltersValue;
        dispatch(updateCollaborationsFilters({ ...decodedFilters, page: 1 }))
    }, [queryFilters])

    const filtersDropdown = useRef<any>(null);

    const closeFiltersDropdown = () => {
        if (filtersDropdown.current) {
            filtersDropdown.current.toggle();
        }
    };

    const simpleBarRef = useRef<any>(null);
    const [scrollContainerId, setScrollContainerId] = useState('');

    useEffect(() => {
        if (simpleBarRef.current) {
            const scrollEl = simpleBarRef.current.getScrollElement();
            scrollEl.id = 'simplebar-scrollable-div';
            setScrollContainerId('simplebar-scrollable-div');
        }
    }, []);

    const { campaignId, influencerId, milestoneId } = useParams();
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (campaignId && influencerId) {
            if (window.innerWidth < 892) {
                milestoneView.current.classList.add("user-chat-show");
            }
        } else {
            if (window.innerWidth < 892) {
                milestoneView.current.classList.remove("user-chat-show");
            }
        }
    }, [campaignId, influencerId]);

    const filters = useSelector((state: RootState) => state.collaborations.filters);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));




    const collaborationsData = useSelector((state: RootState) => state.collaborations.filtered_lists[filterId!]);
    const collaborations = collaborationsData?.list ?? [];
    const page = collaborationsData?.page ?? 1;
    const has_more = collaborationsData?.has_more ?? true;

    const loading = useSelector((state: RootState) => state.collaborations.loading);
    const error = useSelector((state: RootState) => state.collaborations.error);
    const [autoFetchReq, setAutoFetchRequest] = useState(false);


    const setFilters = (updatedFilters: MilestoneCollaborationsFilters) => {

        const encodedFilters = encodeBase64(updatedFilters);

        setSearchParams({ filters: encodedFilters });
        // dispatch(updateCollaborationsFilters(updatedFilters));
    }

    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        if (filters.page && filters.page === 1) {
            setInitialLoading(true);
        }
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchAllCollaborationsThunk({ filters })).finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']));
                setInitialLoading(false);
            })
        }, 500)

    }, [filters])

    useEffect(() => {
        if (filters.q) {
            setSearchQuery(filters.q);
        }
    }, [filters.q])

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchAllCollaborationsThunk({ filters: { ...filters, page: nextPageNumber } }))
        }
    }


    useIdleTimer({
        timeout: 1000 * 60 * 5, // 5 minutes
        onActive: () => {
            setAutoFetchRequest(true);
            setTimeout(() => {
                fetchCollaborations(1, true);
            }, 100)
        },
        throttle: 1000,
    });

    const fetchCollaborations = async (page: number, force: boolean = false) => {
        dispatch(fetchAllCollaborationsThunk({ filters: { ...filters, page }, force })).finally(() => {
            setInitialLoading(false);
            setAutoFetchRequest(false);
        });
    }


    const forceRefetch = () => {
        setInitialLoading(true)
        fetchCollaborations(1, true)
    }

    const searchTask = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...filters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...filters, q: '', page: 1 });
            }
        }, 500);
        setSearchQuery(q);
    }

    return (
        <div className="page-content">
            <Container fluid>
                <div className="chat-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1">
                    <div className="chat-leftsidebar">
                        <div className="p-3 pb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="m-0">Tasks</h5> <Badge color="success" className="m-1">{collaborationsData?.count ?? 0}</Badge>
                                </div>
                                <div className="search-box flex-grow-1">
                                    <input
                                        id="search-user"
                                        type="text"
                                        className="form-control bg-light border-light"
                                        placeholder="Search..."
                                        value={searchQuery}
                                        onChange={(e) => searchTask(e.target.value)}
                                    />
                                    <i className="ri-search-2-line search-icon"></i>
                                    {
                                        searchQuery &&
                                        <i className="ri-close-line clear-search-icon" onClick={() => searchTask('')}></i>
                                    }
                                </div>

                            </div>
                            <div className='d-flex align-items-center justify-content-between gap-2 mb-1'>
                                {/* */}
                                <button title='Refresh' onClick={() => forceRefetch()} className="btn btn-gray p-1 p-md-2"><i className='fs-4 bx bx-refresh text-primary' ></i></button>
                                <UncontrolledDropdown className='sort-dropdown flex-grow-1'>
                                    <DropdownToggle tag={"button"} className='btn btn-gray w-100 d-flex justify-content-between align-items-center p-1 p-md-2'>
                                        <span className='d-flex align-items-center gap-2'>
                                            <span className='text-muted'>Sort by: </span>
                                            {MilestoneCollaborationSortOptionLabel[filters.sort]} <i className={`mdi fs-5 ${filters.order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'}`}></i>
                                        </span>
                                        <i className='mdi mdi-chevron-down fs-5' />
                                    </DropdownToggle>
                                    <DropdownMenu className='mt-1 p-0 w-100'>
                                        {
                                            Object.values(MilestoneCollaborationSortOptions).map((sortOption, i) => {
                                                return (
                                                    <React.Fragment key={i}>
                                                        {
                                                            ['asc', 'desc'].map((order, orderIndex) => {
                                                                return (
                                                                    <DropdownItem key={orderIndex} className='d-flex align-items-center justify-content-between' onClick={() => setFilters({ ...filters, sort: sortOption, order: order as 'asc' | 'desc' })}>
                                                                        <span>
                                                                            {MilestoneCollaborationSortOptionLabel[sortOption]}
                                                                        </span>
                                                                        <i className={`mdi fs-5 ${order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'}`}></i>
                                                                    </DropdownItem>
                                                                )
                                                            })
                                                        }
                                                    </React.Fragment>
                                                )
                                            })
                                        }
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                                <button className='btn btn-gray p-1 p-md-2' onClick={() => showFiltersModal(true)}><FilterIcon className="me-1" /> Filters</button>
                            </div>
                        </div>
                        <SimpleBar className="chat-room-list pt-3" ref={simpleBarRef} style={{ margin: "-16px 0px 0px" }}>
                            {scrollContainerId && (
                                <InfiniteScroll
                                    dataLength={collaborations.length}
                                    next={nextPage}
                                    hasMore={has_more}
                                    loader={<></>}
                                    className='list'
                                    scrollableTarget={scrollContainerId}
                                >
                                    <div className="chat-message-list">
                                        <ul className="list-unstyled chat-list chat-user-list users-list" id="userList">
                                            {!initialLoading && (collaborations || []).map((collaboration, i) => (
                                                <CollaborationListItem collaboration={collaboration} key={i} />
                                            ))}
                                            {
                                                (loading || initialLoading) && !autoFetchReq && Array.from({ length: initialLoading ? 14 : 4 }).map((_, i) => (
                                                    <li key={i} style={{ height: '56px' }}>
                                                        <Skeleton height={55} className='w-100 d-block' />
                                                    </li>
                                                ))
                                            }
                                            {
                                                !(loading || initialLoading) && collaborations.length === 0 &&
                                                <div className="d-flex w-100 justify-content-center align-items-center">
                                                    <div className="length-zero">
                                                        <h3>Currently there is no collaboration in your list, try adjusting filters or search query!</h3>
                                                    </div>
                                                </div>
                                            }
                                            {
                                                !loading && error &&
                                                <div className="d-flex justify-content-center align-items-center">
                                                    <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                                                </div>
                                            }
                                        </ul>
                                    </div>
                                </InfiniteScroll>
                            )}
                        </SimpleBar>
                    </div>
                    <div className="user-chat w-100 overflow-hidden" ref={milestoneView}>
                        {children}
                    </div>
                </div>
            </Container >
            <TaskFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={filters}
                onApply={(filters) => setFilters(filters)}
            />
        </div >
    )
}

export default CollaborationLayout